import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  PhoneOff,
  Languages,
  MessageSquare,
  Copy,
  Check,
  Loader2,
  Subtitles,
  Users,
  Phone,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoCallPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const websocketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const remoteUserRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const translatedAudioQueueRef = useRef([]);
  const isPlayingAudioRef = useRef(false);
  
  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isTranslationEnabled, setIsTranslationEnabled] = useState(true);
  const [isSubtitlesEnabled, setIsSubtitlesEnabled] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [myLanguage, setMyLanguage] = useState('en');
  const [partnerLanguage, setPartnerLanguage] = useState('hi');
  const [participants, setParticipants] = useState([]);
  const [subtitles, setSubtitles] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [copied, setCopied] = useState(false);
  const [translationStatus, setTranslationStatus] = useState('idle');
  const [processingTime, setProcessingTime] = useState(0);
  const [currentRoomId, setCurrentRoomId] = useState(roomId || null);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [mediaError, setMediaError] = useState(null);
  
  const userId = user?.id || user?.user_id || `guest_${Date.now()}`;

  // ICE servers configuration for NAT traversal
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
    ]
  };

  // Play translated audio with queue to prevent overlap
  const playTranslatedAudio = useCallback((base64Audio) => {
    if (!base64Audio) return;
    
    translatedAudioQueueRef.current.push(base64Audio);
    
    const playNext = () => {
      if (translatedAudioQueueRef.current.length === 0 || isPlayingAudioRef.current) return;
      
      isPlayingAudioRef.current = true;
      const audioData = translatedAudioQueueRef.current.shift();
      
      const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
      audio.onended = () => {
        isPlayingAudioRef.current = false;
        playNext();
      };
      audio.onerror = () => {
        isPlayingAudioRef.current = false;
        playNext();
      };
      audio.play().catch(() => {
        isPlayingAudioRef.current = false;
        playNext();
      });
    };
    
    playNext();
  }, []);

  // Add subtitle with auto-remove
  const addSubtitle = useCallback((text, from, language) => {
    if (!text || !isSubtitlesEnabled) return;
    
    const subtitle = {
      id: Date.now(),
      text,
      from,
      language,
      timestamp: new Date().toISOString()
    };
    
    setSubtitles(prev => [...prev.slice(-4), subtitle]);
    
    setTimeout(() => {
      setSubtitles(prev => prev.filter(s => s.id !== subtitle.id));
    }, 5000);
  }, [isSubtitlesEnabled]);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback(async (event) => {
    try {
      const message = JSON.parse(event.data);
      console.log('[WS] Received:', message.type);
      
      switch (message.type) {
        case 'user-joined':
          setParticipants(prev => {
            if (prev.includes(message.user_id)) return prev;
            return [...prev, message.user_id];
          });
          remoteUserRef.current = message.user_id;
          toast.info(`${message.user_id.substring(0, 8)}... joined the call`);
          
          // If we have local stream, create offer for new participant
          if (localStreamRef.current && peerConnectionRef.current) {
            setTimeout(() => createOffer(message.user_id), 500);
          }
          break;
          
        case 'user-left':
          setParticipants(prev => prev.filter(p => p !== message.user_id));
          if (remoteUserRef.current === message.user_id) {
            remoteUserRef.current = null;
            setIsCallActive(false);
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = null;
            }
          }
          toast.info(`${message.user_id.substring(0, 8)}... left the call`);
          break;
          
        case 'offer':
          console.log('[WebRTC] Received offer from:', message.from);
          remoteUserRef.current = message.from;
          await handleOffer(message.offer, message.from);
          break;
          
        case 'answer':
          console.log('[WebRTC] Received answer from:', message.from);
          await handleAnswer(message.answer);
          break;
          
        case 'ice-candidate':
          console.log('[WebRTC] Received ICE candidate from:', message.from);
          await handleIceCandidate(message.candidate);
          break;
          
        case 'translated-audio':
          if (message.from !== userId && isTranslationEnabled) {
            playTranslatedAudio(message.audio);
            addSubtitle(message.text, message.from, message.target_language);
          }
          break;
          
        case 'subtitle':
          addSubtitle(message.text, message.from, message.language);
          break;
          
        case 'chat':
          setChatMessages(prev => [...prev, {
            from: message.from,
            text: message.message,
            timestamp: message.timestamp
          }]);
          break;
          
        case 'translation-result':
          setTranslationStatus('idle');
          if (message.result?.success) {
            setProcessingTime(message.result.processing_time_ms);
            // Show our own subtitles
            if (message.result.original_text) {
              addSubtitle(message.result.original_text, userId, myLanguage);
            }
          }
          break;
          
        case 'pong':
          break;
          
        default:
          console.log('[WS] Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('[WS] Message parse error:', error);
    }
  }, [userId, isTranslationEnabled, playTranslatedAudio, addSubtitle, myLanguage]);

  // Create WebRTC offer
  const createOffer = useCallback(async (targetUserId) => {
    if (!peerConnectionRef.current || !websocketRef.current) return;
    
    try {
      console.log('[WebRTC] Creating offer for:', targetUserId);
      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);
      
      websocketRef.current.send(JSON.stringify({
        type: 'offer',
        offer: offer,
        target: targetUserId
      }));
    } catch (error) {
      console.error('[WebRTC] Create offer error:', error);
    }
  }, []);

  // Handle incoming offer
  const handleOffer = useCallback(async (offer, fromUserId) => {
    if (!peerConnectionRef.current || !websocketRef.current) return;
    
    try {
      console.log('[WebRTC] Handling offer from:', fromUserId);
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
      
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);
      
      websocketRef.current.send(JSON.stringify({
        type: 'answer',
        answer: answer,
        target: fromUserId
      }));
    } catch (error) {
      console.error('[WebRTC] Handle offer error:', error);
    }
  }, []);

  // Handle incoming answer
  const handleAnswer = useCallback(async (answer) => {
    if (!peerConnectionRef.current) return;
    
    try {
      console.log('[WebRTC] Setting remote description');
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (error) {
      console.error('[WebRTC] Handle answer error:', error);
    }
  }, []);

  // Handle ICE candidate
  const handleIceCandidate = useCallback(async (candidate) => {
    if (!peerConnectionRef.current || !candidate) return;
    
    try {
      await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      console.error('[WebRTC] ICE candidate error:', error);
    }
  }, []);

  // Initialize media stream
  const initializeMedia = useCallback(async () => {
    try {
      setMediaError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      console.log('[Media] Initialized successfully');
      return stream;
    } catch (error) {
      console.error('[Media] Access error:', error);
      setMediaError(error.message || 'Camera/microphone access denied');
      toast.error('Failed to access camera/microphone. Please check permissions.');
      throw error;
    }
  }, []);

  // Create peer connection
  const createPeerConnection = useCallback(() => {
    console.log('[WebRTC] Creating peer connection');
    
    const pc = new RTCPeerConnection(iceServers);
    
    pc.onicecandidate = (event) => {
      if (event.candidate && websocketRef.current && remoteUserRef.current) {
        console.log('[WebRTC] Sending ICE candidate');
        websocketRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          candidate: event.candidate,
          target: remoteUserRef.current
        }));
      }
    };
    
    pc.ontrack = (event) => {
      console.log('[WebRTC] Remote track received');
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };
    
    pc.onconnectionstatechange = () => {
      console.log('[WebRTC] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setIsCallActive(true);
        toast.success('Call connected!');
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        setIsCallActive(false);
        toast.error('Call disconnected');
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC] ICE state:', pc.iceConnectionState);
    };
    
    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }
    
    peerConnectionRef.current = pc;
    return pc;
  }, []);

  // Initialize WebSocket connection
  const connectWebSocket = useCallback((roomIdToJoin) => {
    if (!roomIdToJoin) return;
    
    // Build WebSocket URL
    const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = API_URL.replace('https://', '').replace('http://', '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/video-call/ws/${roomIdToJoin}/${userId}`;
    
    console.log('[WS] Connecting to:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('[WS] Connected');
      setIsConnected(true);
      setConnectionError(null);
      toast.success('Connected to call room');
      
      // Start ping interval
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
      ws._pingInterval = pingInterval;
    };
    
    ws.onclose = (event) => {
      console.log('[WS] Closed:', event.code, event.reason);
      setIsConnected(false);
      clearInterval(ws._pingInterval);
      
      if (event.code !== 1000) {
        toast.error('Disconnected from call');
      }
    };
    
    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
      setConnectionError('WebSocket connection failed');
      toast.error('Connection error. Please try again.');
    };
    
    ws.onmessage = handleWebSocketMessage;
    
    websocketRef.current = ws;
  }, [userId, handleWebSocketMessage]);

  // Send audio for translation
  const sendAudioForTranslation = useCallback(async (audioBlob) => {
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) return;
    if (!isTranslationEnabled || isMuted) return;
    
    setTranslationStatus('processing');
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Audio = reader.result.split(',')[1];
      
      websocketRef.current.send(JSON.stringify({
        type: 'translate',
        audio: base64Audio,
        source_language: myLanguage,
        target_language: partnerLanguage
      }));
    };
    reader.readAsDataURL(audioBlob);
  }, [isTranslationEnabled, isMuted, myLanguage, partnerLanguage]);

  // Start recording for translation (3-second chunks)
  const startRecordingLoop = useCallback(() => {
    if (!localStreamRef.current || isMuted || !isTranslationEnabled || !isCallActive) {
      console.log('[Recording] Conditions not met, skipping');
      return;
    }
    
    const audioTrack = localStreamRef.current.getAudioTracks()[0];
    if (!audioTrack || !audioTrack.enabled) return;
    
    // Don't start if already recording
    if (mediaRecorderRef.current?.state === 'recording') return;
    
    console.log('[Recording] Starting chunk');
    
    try {
      const audioStream = new MediaStream([audioTrack]);
      const mediaRecorder = new MediaRecorder(audioStream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        setIsRecording(false);
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          audioChunksRef.current = [];
          
          // Only send if we have meaningful audio (> 1KB)
          if (audioBlob.size > 1000) {
            await sendAudioForTranslation(audioBlob);
          }
        }
      };
      
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      
      // Stop after 3 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, 3000);
    } catch (error) {
      console.error('[Recording] Error:', error);
      setIsRecording(false);
    }
  }, [isMuted, isTranslationEnabled, isCallActive, sendAudioForTranslation]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
        
        // Stop recording if muted
        if (!audioTrack.enabled && mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }
    }
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  }, []);

  // End call
  const endCall = useCallback(() => {
    console.log('[Call] Ending');
    
    // Stop recording interval
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    
    // Stop media recorder
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    // Stop all media tracks
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    
    // Close peer connection
    peerConnectionRef.current?.close();
    
    // Close WebSocket
    if (websocketRef.current) {
      clearInterval(websocketRef.current._pingInterval);
      websocketRef.current.close(1000);
    }
    
    navigate('/dashboard');
    toast.info('Call ended');
  }, [navigate]);

  // Copy room link
  const copyRoomLink = useCallback(() => {
    const link = `${window.location.origin}/video-call/${currentRoomId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Room link copied! Share with others to join.');
    setTimeout(() => setCopied(false), 2000);
  }, [currentRoomId]);

  // Send chat message
  const sendChatMessage = useCallback(() => {
    if (!chatInput.trim() || !websocketRef.current) return;
    
    websocketRef.current.send(JSON.stringify({
      type: 'chat',
      message: chatInput,
      timestamp: new Date().toISOString()
    }));
    
    setChatMessages(prev => [...prev, {
      from: userId,
      text: chatInput,
      timestamp: new Date().toISOString()
    }]);
    
    setChatInput('');
  }, [chatInput, userId]);

  // Create room if no roomId provided
  useEffect(() => {
    if (!roomId && !isCreatingRoom && !currentRoomId) {
      setIsCreatingRoom(true);
      
      fetch(`${API_URL}/api/video-call/room/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host_id: userId })
      })
        .then(res => res.json())
        .then(room => {
          console.log('[Room] Created:', room.id);
          setCurrentRoomId(room.id);
          navigate(`/video-call/${room.id}`, { replace: true });
        })
        .catch(err => {
          console.error('[Room] Create error:', err);
          toast.error('Failed to create room');
          navigate('/dashboard');
        })
        .finally(() => setIsCreatingRoom(false));
    } else if (roomId && !currentRoomId) {
      setCurrentRoomId(roomId);
    }
  }, [roomId, isCreatingRoom, currentRoomId, userId, navigate]);

  // Initialize call when room is ready
  useEffect(() => {
    if (!currentRoomId || isCreatingRoom) return;
    
    let mounted = true;
    
    const initCall = async () => {
      try {
        await initializeMedia();
        if (!mounted) return;
        
        createPeerConnection();
        connectWebSocket(currentRoomId);
      } catch (error) {
        console.error('[Init] Error:', error);
      }
    };
    
    initCall();
    
    return () => {
      mounted = false;
    };
  }, [currentRoomId, isCreatingRoom, initializeMedia, createPeerConnection, connectWebSocket]);

  // Handle recording loop when call becomes active
  useEffect(() => {
    if (isCallActive && isTranslationEnabled && !isMuted) {
      // Start recording loop
      recordingIntervalRef.current = setInterval(() => {
        if (!isRecording) {
          startRecordingLoop();
        }
      }, 3500);
      
      // Start first recording
      startRecordingLoop();
    } else {
      // Stop recording loop
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    }
    
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [isCallActive, isTranslationEnabled, isMuted, isRecording, startRecordingLoop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      localStreamRef.current?.getTracks().forEach(track => track.stop());
      peerConnectionRef.current?.close();
      if (websocketRef.current) {
        clearInterval(websocketRef.current._pingInterval);
        websocketRef.current.close(1000);
      }
    };
  }, []);

  // Loading state
  if (!currentRoomId || isCreatingRoom) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mx-auto mb-4" />
          <p className="text-white">Setting up your call...</p>
        </div>
      </div>
    );
  }

  // Media error state
  if (mediaError) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <VideoOff className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Camera Access Required</h2>
          <p className="text-gray-400 mb-6">{mediaError}</p>
          <p className="text-sm text-gray-500 mb-6">
            Please allow camera and microphone access in your browser settings, then refresh the page.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 inline mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030014] flex flex-col" data-testid="video-call-page">
      {/* Header */}
      <header className="h-16 bg-black/50 border-b border-white/10 flex items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </button>
          
          <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
          <span className="text-white font-medium text-sm md:text-base">
            {isConnected ? (isCallActive ? 'In Call' : 'Waiting...') : 'Connecting...'}
          </span>
          {participants.length > 0 && (
            <span className="text-gray-400 text-sm hidden md:inline">
              <Users className="w-4 h-4 inline mr-1" />
              {participants.length + 1}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2 md:gap-3">
          {/* Room ID & Copy */}
          <div className="flex items-center gap-2 px-2 md:px-3 py-1.5 bg-white/5 rounded-lg">
            <span className="text-sm text-gray-400 hidden md:inline">Room:</span>
            <code className="text-xs md:text-sm text-cyan-400">{currentRoomId}</code>
            <button
              onClick={copyRoomLink}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              data-testid="copy-link-btn"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
          
          {/* Translation Status */}
          {isTranslationEnabled && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <Languages className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300">
                {myLanguage === 'en' ? 'EN' : 'HI'} → {partnerLanguage === 'en' ? 'EN' : 'HI'}
              </span>
              {translationStatus === 'processing' && (
                <Loader2 className="w-3 h-3 text-purple-400 animate-spin" />
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Video Area */}
      <main className="flex-1 relative p-2 md:p-4 overflow-hidden">
        <div className="h-full flex flex-col md:flex-row gap-2 md:gap-4">
          {/* Remote Video (Large) */}
          <div className="flex-1 relative bg-gray-900 rounded-xl md:rounded-2xl overflow-hidden min-h-[300px]">
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
              data-testid="remote-video"
            />
            
            {!isCallActive && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <div className="text-center p-6">
                  <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-10 h-10 text-gray-500" />
                  </div>
                  <p className="text-white text-lg mb-2">Waiting for participant...</p>
                  <p className="text-gray-400 text-sm mb-4">Share the room link to invite someone</p>
                  <button
                    onClick={copyRoomLink}
                    className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm"
                  >
                    <Copy className="w-4 h-4 inline mr-2" />
                    Copy Invite Link
                  </button>
                </div>
              </div>
            )}
            
            {/* Subtitles Overlay */}
            <AnimatePresence>
              {isSubtitlesEnabled && subtitles.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 w-[90%] max-w-2xl"
                  data-testid="subtitles-container"
                >
                  {subtitles.map(sub => (
                    <motion.div
                      key={sub.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="bg-black/80 backdrop-blur-sm px-4 md:px-6 py-2 md:py-3 rounded-lg text-center mb-2"
                    >
                      <p className="text-white text-sm md:text-lg">{sub.text}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {sub.from === userId ? 'You' : 'Partner'} • {sub.language === 'en' ? 'English' : 'Hindi'}
                      </p>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Recording indicator */}
            {isRecording && isTranslationEnabled && (
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-full">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-xs text-red-400">Recording</span>
              </div>
            )}
          </div>

          {/* Sidebar - Local Video & Settings */}
          <div className="w-full md:w-72 flex flex-col gap-2 md:gap-4">
            {/* Local Video (Small) */}
            <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-video">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover scale-x-[-1]"
                data-testid="local-video"
              />
              {isVideoOff && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <VideoOff className="w-8 h-8 text-gray-500" />
                </div>
              )}
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white">
                You {isMuted && '(Muted)'}
              </div>
            </div>

            {/* Language Settings */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 md:p-4">
              <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Languages className="w-4 h-4 text-purple-400" />
                Translation
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-400">You speak</label>
                  <select
                    value={myLanguage}
                    onChange={(e) => setMyLanguage(e.target.value)}
                    className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                    data-testid="my-language-select"
                  >
                    <option value="en" className="bg-gray-900">English</option>
                    <option value="hi" className="bg-gray-900">Hindi (हिंदी)</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-xs text-gray-400">Partner speaks</label>
                  <select
                    value={partnerLanguage}
                    onChange={(e) => setPartnerLanguage(e.target.value)}
                    className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                    data-testid="partner-language-select"
                  >
                    <option value="hi" className="bg-gray-900">Hindi (हिंदी)</option>
                    <option value="en" className="bg-gray-900">English</option>
                  </select>
                </div>
                
                {processingTime > 0 && (
                  <p className="text-xs text-gray-500">
                    Last translation: {(processingTime / 1000).toFixed(1)}s
                  </p>
                )}
              </div>
            </div>

            {/* Chat Panel */}
            {showChat && (
              <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col max-h-64 md:max-h-none">
                <h3 className="text-sm font-medium text-white mb-2">Chat</h3>
                <div className="flex-1 overflow-y-auto space-y-2 mb-2 min-h-[100px]">
                  {chatMessages.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-4">No messages yet</p>
                  ) : (
                    chatMessages.map((msg, i) => (
                      <div key={i} className={`text-sm ${msg.from === userId ? 'text-right' : ''}`}>
                        <span className={`inline-block px-3 py-1.5 rounded-lg max-w-[80%] ${
                          msg.from === userId 
                            ? 'bg-cyan-500/20 text-cyan-300' 
                            : 'bg-white/10 text-white'
                        }`}>
                          {msg.text}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                    placeholder="Type message..."
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                    data-testid="chat-input"
                  />
                  <button
                    onClick={sendChatMessage}
                    className="px-3 py-2 bg-cyan-500 text-white rounded-lg text-sm hover:bg-cyan-600 transition-colors"
                    data-testid="send-chat-btn"
                  >
                    Send
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Controls Bar */}
      <footer className="h-20 md:h-24 bg-black/50 border-t border-white/10 flex items-center justify-center gap-2 md:gap-4 px-4">
        {/* Mute */}
        <button
          onClick={toggleMute}
          className={`p-3 md:p-4 rounded-full transition-all ${
            isMuted 
              ? 'bg-red-500/20 text-red-400 ring-2 ring-red-500/50' 
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          data-testid="mute-btn"
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <MicOff className="w-5 h-5 md:w-6 md:h-6" /> : <Mic className="w-5 h-5 md:w-6 md:h-6" />}
        </button>
        
        {/* Video */}
        <button
          onClick={toggleVideo}
          className={`p-3 md:p-4 rounded-full transition-all ${
            isVideoOff 
              ? 'bg-red-500/20 text-red-400 ring-2 ring-red-500/50' 
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          data-testid="video-btn"
          title={isVideoOff ? 'Turn on video' : 'Turn off video'}
        >
          {isVideoOff ? <VideoOff className="w-5 h-5 md:w-6 md:h-6" /> : <Video className="w-5 h-5 md:w-6 md:h-6" />}
        </button>
        
        {/* Translation Toggle */}
        <button
          onClick={() => setIsTranslationEnabled(!isTranslationEnabled)}
          className={`p-3 md:p-4 rounded-full transition-all ${
            isTranslationEnabled 
              ? 'bg-purple-500/20 text-purple-400 ring-2 ring-purple-500/50' 
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          data-testid="translation-btn"
          title={isTranslationEnabled ? 'Disable translation' : 'Enable translation'}
        >
          <Languages className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        
        {/* Subtitles Toggle */}
        <button
          onClick={() => setIsSubtitlesEnabled(!isSubtitlesEnabled)}
          className={`p-3 md:p-4 rounded-full transition-all ${
            isSubtitlesEnabled 
              ? 'bg-cyan-500/20 text-cyan-400 ring-2 ring-cyan-500/50' 
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          data-testid="subtitles-btn"
          title={isSubtitlesEnabled ? 'Hide subtitles' : 'Show subtitles'}
        >
          <Subtitles className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        
        {/* Chat */}
        <button
          onClick={() => setShowChat(!showChat)}
          className={`p-3 md:p-4 rounded-full transition-all ${
            showChat 
              ? 'bg-cyan-500/20 text-cyan-400 ring-2 ring-cyan-500/50' 
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          data-testid="chat-btn"
          title={showChat ? 'Hide chat' : 'Show chat'}
        >
          <MessageSquare className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        
        {/* End Call */}
        <button
          onClick={endCall}
          className="p-3 md:p-4 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors ml-2 md:ml-4"
          data-testid="end-call-btn"
          title="End call"
        >
          <PhoneOff className="w-5 h-5 md:w-6 md:h-6" />
        </button>
      </footer>
    </div>
  );
}
